import React, { useEffect, useRef, useState } from 'react';
import { Select, InputNumber, Button, Space, Tag, Empty } from 'antd';
import { EnvironmentOutlined, DeleteOutlined } from '@ant-design/icons';
import { metaAdsApi } from '../../utils/api';

/**
 * Ciblage géographique par villes avec rayon.
 *
 * Les villes proviennent du référentiel Meta : chacune porte une clé (`key`) obligatoire
 * pour le ciblage, qui ne peut donc pas être saisie librement — d'où la recherche en direct.
 *
 * Important : dès qu'au moins une ville est sélectionnée, Meta ignore le ciblage par pays
 * (les deux ne peuvent pas coexister dans un même ensemble de publicités).
 *
 * @param {Array}    villes   - Villes sélectionnées [{ key, name, radius, distanceUnit }]
 * @param {Function} onChange - Appelé avec la nouvelle liste
 */
const CiblageVilles = ({ villes = [], onChange }) => {
  const [resultats, setResultats] = useState([]);
  const [recherche, setRecherche] = useState(false);
  // Référence du délai en attente pour n'envoyer qu'une recherche après la frappe.
  const delaiRechercheRef = useRef(null);
  // Numéro de la dernière recherche, utilisé pour ignorer une réponse devenue obsolète.
  const requeteActiveRef = useRef(0);

  /**
   * Annule le délai restant lorsque le composant disparaît afin d'éviter un appel
   * réseau et une mise à jour d'état après la fermeture de la revue de campagne.
   */
  useEffect(() => {
    return () => {
      if (delaiRechercheRef.current) clearTimeout(delaiRechercheRef.current);
      requeteActiveRef.current += 1;
    };
  }, []);

  /**
   * Programme une recherche dans Meta après une courte pause de frappe. Cela limite
   * les requêtes API sans retarder sensiblement la recherche de villes.
   * @param {string} texte
   */
  const rechercherVilles = (texte) => {
    if (delaiRechercheRef.current) clearTimeout(delaiRechercheRef.current);
    const numeroRequete = requeteActiveRef.current + 1;
    requeteActiveRef.current = numeroRequete;

    if (!texte || texte.trim().length < 2) {
      setResultats([]);
      setRecherche(false);
      return;
    }

    setRecherche(true);
    delaiRechercheRef.current = setTimeout(async () => {
      try {
        const { data } = await metaAdsApi.searchGeoLocations(texte.trim(), 'city');
        // Une réponse d'une frappe précédente ne doit jamais écraser la recherche actuelle.
        if (requeteActiveRef.current === numeroRequete) setResultats(data.data || []);
      } catch (error) {
        console.error('Erreur lors de la recherche de villes:', error);
        if (requeteActiveRef.current === numeroRequete) setResultats([]);
      } finally {
        if (requeteActiveRef.current === numeroRequete) {
          setRecherche(false);
          delaiRechercheRef.current = null;
        }
      }
    }, 350);
  };

  /**
   * Ajoute une ville à la sélection, avec un rayon par défaut de 25 km.
   * @param {string} cle - Clé Meta de la ville
   */
  const ajouterVille = (cle) => {
    const trouvee = resultats.find(r => r.key === cle);
    if (!trouvee || villes.some(v => v.key === cle)) return;

    onChange([
      ...villes,
      {
        key: trouvee.key,
        name: [trouvee.name, trouvee.region, trouvee.country].filter(Boolean).join(', '),
        radius: 25,
        distanceUnit: 'kilometer'
      }
    ]);
  };

  /** Retire une ville de la sélection */
  const retirerVille = (cle) => {
    onChange(villes.filter(v => v.key !== cle));
  };

  /** Modifie le rayon de diffusion autour d'une ville */
  const modifierRayon = (cle, rayon) => {
    onChange(villes.map(v => (v.key === cle ? { ...v, radius: rayon || 25 } : v)));
  };

  return (
    <div className="space-y-3">
      <Select
        showSearch
        value={null}
        placeholder="Rechercher une ville (ex : Abidjan, Yamoussoukro)"
        style={{ width: '100%' }}
        filterOption={false}
        loading={recherche}
        onSearch={rechercherVilles}
        onChange={ajouterVille}
        notFoundContent={recherche ? 'Recherche…' : null}
        suffixIcon={<EnvironmentOutlined />}
        options={resultats.map(lieu => ({
          value: lieu.key,
          label: [lieu.name, lieu.region, lieu.country].filter(Boolean).join(', ')
        }))}
      />

      {villes.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Aucune ville : le ciblage se fait sur le ou les pays indiqués"
        />
      ) : (
        <>
          <div className="space-y-2">
            {villes.map(ville => (
              <div key={ville.key} className="flex items-center gap-2 p-2 bg-white rounded border">
                <Tag icon={<EnvironmentOutlined />} color="blue" className="private-tag-accent flex-1 truncate">
                  {ville.name}
                </Tag>
                <Space>
                  <span className="text-sm text-gray-500">Rayon</span>
                  <InputNumber
                    min={1}
                    max={80}
                    value={ville.radius}
                    onChange={valeur => modifierRayon(ville.key, valeur)}
                    addonAfter="km"
                    style={{ width: 120 }}
                  />
                  <Button
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => retirerVille(ville.key)}
                  />
                </Space>
              </div>
            ))}
          </div>
          <div className="text-xs text-orange-700">
            Le ciblage par villes remplace le ciblage par pays pour cet ensemble de publicités.
            Rayon maximum accepté par Meta : 80 km.
          </div>
        </>
      )}
    </div>
  );
};

export default CiblageVilles;
